import { formatMoneyValue } from '@utils/moneyFormatting';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import type { PortfolioAuditTradeSummary } from '../../utils/audit/portfolioAuditSummary';

export type BulkRebalanceTradeActivityLineProps = {
  summary: Pick<
    PortfolioAuditTradeSummary,
    'tradeCount' | 'totalBuyCents' | 'totalSellCents'
  >;
  layout?: 'stacked' | 'inline';
  testId?: string;
};

const TradeAmountSeparator = () => {
  const theme = useTheme();

  return (
    <Box component='span' sx={{ color: theme.color.outlineVariant }}>
      {' · '}
    </Box>
  );
};

export const BulkRebalanceTradeActivityLine = ({
  summary,
  layout = 'stacked',
  testId,
}: BulkRebalanceTradeActivityLineProps) => {
  const { t } = useTranslation(['bulkRebalance']);
  const theme = useTheme();

  if (layout === 'inline') {
    const hasSell = summary.totalSellCents > 0;
    const hasBuy = summary.totalBuyCents > 0;
    const hasTradeCount = summary.tradeCount > 0;

    if (!hasSell && !hasBuy && !hasTradeCount) {
      return null;
    }

    return (
      <Typography
        variant='bodySmall'
        data-testid={testId ?? 'bulk-rebalance-trade-summary-card'}
      >
        {hasSell && (
          <Box component='span' sx={{ color: theme.color.negative }}>
            −{formatMoneyValue(summary.totalSellCents)}
          </Box>
        )}
        {hasSell && hasBuy && <TradeAmountSeparator />}
        {hasBuy && (
          <Box component='span' sx={{ color: theme.color.positive }}>
            +{formatMoneyValue(summary.totalBuyCents)}
          </Box>
        )}
        {hasTradeCount && (
          <>
            {(hasSell || hasBuy) && <TradeAmountSeparator />}
            <Box component='span' sx={{ color: theme.color.onSurfaceVariant }}>
              {t('audit.proposedTrades', { count: summary.tradeCount })}
            </Box>
          </>
        )}
      </Typography>
    );
  }

  return (
    <Box
      data-testid={testId ?? 'bulk-rebalance-trade-activity-line'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 0.5,
      }}
    >
      <Typography variant='bodySmall' color='textSecondary'>
        {t('audit.proposedTrades', { count: summary.tradeCount })}
      </Typography>
      {(summary.totalBuyCents > 0 || summary.totalSellCents > 0) && (
        <Typography variant='labelSmall'>
          {summary.totalBuyCents > 0 && (
            <Box component='span' sx={{ color: theme.color.positive }}>
              +{formatMoneyValue(summary.totalBuyCents)}
            </Box>
          )}
          {summary.totalBuyCents > 0 && summary.totalSellCents > 0 && (
            <TradeAmountSeparator />
          )}
          {summary.totalSellCents > 0 && (
            <Box component='span' sx={{ color: theme.color.negative }}>
              -{formatMoneyValue(summary.totalSellCents)}
            </Box>
          )}
        </Typography>
      )}
    </Box>
  );
};
